import { useState } from 'react';
import { Phone, MessageSquare } from 'lucide-react';
import { Dialog, DialogTrigger, DialogContent, DialogTitle, DialogDescription, DialogClose } from './ui/dialog';
import { GOOGLE_BUSINESS } from '../config/googleBusiness';
import { redirectToPhoneApp } from '../shared/phoneAppRedirect';
import { useStoreHours } from '../shared/useStoreHours';

export const HomeContactChoice = ({ kind, idPrefix, appearance = 'link' }) => {
  const [handoffError, setHandoffError] = useState(false);
  const open = useStoreHours();
  const prefix = idPrefix || `homepage-${kind}`;
  const number = `+1${GOOGLE_BUSINESS.phoneRaw}`;
  const subject = kind === 'repair' ? 'repairs' : 'selling a device';
  const triggerColor = kind === 'repair'
    ? 'text-blue-700 hover:text-blue-900'
    : 'text-emerald-700 hover:text-emerald-900';

  const openPhoneApp = (event) => {
    // Explicitly navigate ONCE, during the tap, instead of relying on a modal's
    // default anchor navigation inside an embedded/in-app browser.
    event.preventDefault();
    setHandoffError(!redirectToPhoneApp(event.currentTarget.href));
  };

  return (
    <Dialog onOpenChange={() => setHandoffError(false)}>
      <DialogTrigger asChild>
        <button type="button" data-testid={`${prefix}-call-button`}
          aria-label={`Call or text us about ${subject}`}
          className={appearance === 'button'
            ? 'inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg border border-emerald-700 bg-emerald-50 px-3 py-2 text-sm font-bold text-emerald-800 transition-colors hover:bg-emerald-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700'
            : `mt-1 inline-flex min-h-[44px] items-center justify-center gap-2 text-sm font-bold hover:underline underline-offset-4 transition-colors ${triggerColor}`}>
          <Phone className="w-4 h-4 shrink-0" aria-hidden="true" /> Call Us Now
        </button>
      </DialogTrigger>
      <DialogContent data-testid={`${prefix}-contact-dialog`}
        closeTestId={`${prefix}-contact-close`} overlayTestId={`${prefix}-contact-overlay`}
        style={{ transform: 'translate(-50%, -50%)' }}
        className="w-[calc(100%_-_2rem)] max-w-sm max-h-[90vh] overflow-y-auto rounded-lg border-slate-200 bg-white p-6 text-slate-900">
        <section className="space-y-2 pr-6">
          <DialogTitle className="text-lg tracking-normal" data-testid={`${prefix}-contact-title`}>Call or text us</DialogTitle>
          <DialogDescription className="text-sm text-slate-600" data-testid={`${prefix}-contact-number`}>
            {GOOGLE_BUSINESS.phone}
          </DialogDescription>
        </section>
        {!open && (
          <p role="status" data-testid={`${prefix}-after-hours-message`}
            className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm leading-relaxed text-emerald-900">
            We’re closed right now. Texting is recommended — we’ll respond during business hours.
          </p>
        )}
        <section className="grid grid-cols-2 gap-3 pt-2">
          <a href={`tel:${number}`} target="_top" onClick={openPhoneApp} data-testid={`${prefix}-contact-call`} data-openai-call-source={prefix}
            aria-label={`Call ${GOOGLE_BUSINESS.phone}`}
            className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700">
            <Phone className="h-5 w-5 shrink-0" aria-hidden="true" /> Call
          </a>
          <a href={`sms:${number}`} target="_top" onClick={openPhoneApp} data-testid={`${prefix}-contact-text`} data-openai-text-source={prefix}
            aria-label={`Text ${GOOGLE_BUSINESS.phone}`}
            className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-lg border border-emerald-700 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800 transition-colors hover:bg-emerald-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700">
            <MessageSquare className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span className="flex min-w-0 flex-col items-center">
              <span>Text</span>
              {!open && <span data-testid={`${prefix}-text-recommended`} className="text-[10px] font-medium leading-4">Recommended</span>}
            </span>
          </a>
        </section>
        {handoffError && (
          <p role="alert" data-testid={`${prefix}-contact-error`} className="text-sm text-red-700">
            This browser blocked the redirect. Please open this page in your phone’s browser.
          </p>
        )}
        <DialogClose asChild>
          <button type="button" data-testid={`${prefix}-contact-cancel`}
            className="min-h-[44px] rounded-lg text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-700">
            Cancel
          </button>
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
};